import isObject from 'lodash/isObject';
import { getLocalMerchantId } from 'components/settings/localStore';
import { SignUpFormData } from 'components/user/model/User';
import { isDefined } from 'lib/typeInference';

interface ILocalAuth {
    userId: string;
    token: string;
    expiry: number;
    isGuest: boolean;
}

// Some values live in a single localStorage entry shared across merchants,
// stored as a JSON record keyed by merchantId.
const getMerchantScopedValue = <T>(storageKey: string): T | null => {
    const merchantId = getLocalMerchantId();
    const record = localStorage.getItem(storageKey);
    if (record && merchantId) {
        const value = JSON.parse(record)[merchantId];
        if (isDefined(value)) {
            return value as T;
        }
    }
    return null;
};

const setMerchantScopedValue = <T>(storageKey: string, value: T): T | null => {
    const merchantId = getLocalMerchantId();
    if (merchantId) {
        const record = JSON.parse(localStorage.getItem(storageKey) || '{}');
        record[merchantId] = value;
        localStorage.setItem(storageKey, JSON.stringify(record));
        return value;
    }
    return null;
};

const removeMerchantScopedValue = (storageKey: string) => {
    const merchantId = getLocalMerchantId();
    const record = localStorage.getItem(storageKey);
    if (record && merchantId) {
        const parsedRecord = JSON.parse(record);
        delete parsedRecord[merchantId];
        if (Object.keys(parsedRecord).length > 0) {
            localStorage.setItem(storageKey, JSON.stringify(parsedRecord));
        } else {
            localStorage.removeItem(storageKey);
        }
        return parsedRecord;
    }
    return null;
};

// The identity (email/phone) a user registered with, kept until activation completes.
// Stored under the legacy 'userId' key so in-flight signups survive this rename.
const PENDING_SIGNUP_IDENTITY_KEY = 'userId';

export const setPendingSignupIdentity = (identity: string) => {
    setMerchantScopedValue(PENDING_SIGNUP_IDENTITY_KEY, identity);
};

export const getPendingSignupIdentity = (): string =>
    getMerchantScopedValue<string>(PENDING_SIGNUP_IDENTITY_KEY) ?? '';

export const clearPendingSignupIdentity = () => {
    removeMerchantScopedValue(PENDING_SIGNUP_IDENTITY_KEY);
};

// TODO: I would move them all to variables;
const ON_CLOSE_AUTH_REDIRECT = 'ON_CLOSE_AUTH_REDIRECT-R';

export const setOnCloseRedirectUrl = (path: string, merchantId: string) => {
    localStorage.setItem(`${ON_CLOSE_AUTH_REDIRECT}-${merchantId}`, path);
};

export const getOnCloseRedirectUrl = (merchantId: string) =>
    localStorage.getItem(`${ON_CLOSE_AUTH_REDIRECT}-${merchantId}`);

export const deleteOnCloseRedirectUrl = (merchantId: string) => {
    localStorage.removeItem(`${ON_CLOSE_AUTH_REDIRECT}-${merchantId}`);
};

export const setShouldUpdateGuestSession = (merchantId: string) =>
    localStorage.setItem(`UPDATE-GUEST-SESSION-${merchantId}`, 'true');

export const getShouldUpdateGuestSession = (merchantId: string) =>
    localStorage.getItem(`UPDATE-GUEST-SESSION-${merchantId}`);

export const resetShouldUpdateGuestSession = (merchantId: string) =>
    localStorage.removeItem(`UPDATE-GUEST-SESSION-${merchantId}`);

export const setSignupDataToSession = (merchantId: string, userData: Partial<SignUpFormData>) => {
    sessionStorage.setItem(`USER-SIGNUP-DATA-${merchantId}`, JSON.stringify(userData));
};

export const getSignupDataFromSession = (merchantId: string) =>
    sessionStorage.getItem(`USER-SIGNUP-DATA-${merchantId}`);

export const clearSignupDataFromSession = (merchantId: string) => {
    sessionStorage.removeItem(`USER-SIGNUP-DATA-${merchantId}`);
};

const AUTHENTICATION_KEY = 'authentication';

export const setLocalAuthentication = (value: ILocalAuth): ILocalAuth | null =>
    setMerchantScopedValue(AUTHENTICATION_KEY, value);

export const getLocalAuthentication = (): ILocalAuth | null => {
    const authentication = getMerchantScopedValue<ILocalAuth>(AUTHENTICATION_KEY);
    return isObject(authentication) ? authentication : null;
};

export const updateLocalAuthentication = (value: Partial<ILocalAuth>): ILocalAuth | null => {
    const authentication = getLocalAuthentication();
    if (authentication) {
        return setMerchantScopedValue(AUTHENTICATION_KEY, { ...authentication, ...value });
    }
    return null;
};

export const resetLocalAuthentication = () => removeMerchantScopedValue(AUTHENTICATION_KEY);
